import _ from "lodash";
import React from "react";
import Taxonomy from "./Taxonomy";
import CheckForError from "./CheckForError";
import { PLATFORM_MAP, GCM_PRISMA_COLUMNS } from "./constants";
import { TaxonomyCellRenderer } from "../ViewMasterCampaigns/utils";
// inspect the data looking for discrepencies. If found, record it in the output array.
const getDiscrepantColumnKeys = ({ data, platform, columns }) =>
	data.reduce((output, datum) => {
		// segregate the data into pm and ep buckets to prepare for comparison
		const pmEntries = [];
		const epEntries = [];
		Object.entries(datum)?.forEach((entry) => {
			entry?.[0].startsWith(PLATFORM_MAP.OS.prefix) && pmEntries?.push(entry);
			entry?.[0].startsWith(platform.prefix) && epEntries?.push(entry);
		});

		if (pmEntries?.length == epEntries?.length) {
			// compare platform values for each key, preparsing if required.
			pmEntries.forEach((entry, i) => {
				const [sourceKey, sourceValue] = entry;
				const [testKey, testValue] = epEntries[i];
				const baseSourceKey = sourceKey.replace(PLATFORM_MAP.OS.prefix, "");
				const columnData = columns.find(({ key }) => key == baseSourceKey);

				// ignore data keys that are not apart of column data, and only check keys not previously discrepant.
				if (columnData && !output.includes(baseSourceKey)) {
					const hasDiscrepency =
						"parser" in columnData ? columnData.parser(sourceValue, platform.id) !== columnData.parser(testValue, platform.id) : sourceValue !== testValue;

					// record that this column has a discrepancy
					hasDiscrepency && output.push(baseSourceKey);
				}
			});
		}
		return output;
	}, []);

// use the constant column data along with the discrepencies and flags from the app to build columns ready to be built into actual columns
const getEnrichedColumnData = ({ selectedPlatformKey, columnTypeKey, tabKey, discrepantColumnKeys, alwaysShowBaseColumn }) =>
	PLATFORM_MAP?.[selectedPlatformKey]?.[columnTypeKey]?.[tabKey]?.reduce((acc, column) => {
		const basePlatformColumnData = {
			...column,
			sourcePlatformPrefix: PLATFORM_MAP.OS?.prefix,
			testPlatformPrefix: PLATFORM_MAP[selectedPlatformKey]?.prefix,
			platform: selectedPlatformKey
		};

		const externalPlatformColumnData = {
			...column,
			sourcePlatformPrefix: PLATFORM_MAP[selectedPlatformKey]?.prefix,
			testPlatformPrefix: PLATFORM_MAP.OS?.prefix,
			className: PLATFORM_MAP[selectedPlatformKey]?.platformClass,
			platform: selectedPlatformKey
		};

		// if there is a discrepency, show both the base and external columns.
		if (discrepantColumnKeys.includes(column.key)) {
			acc.push(basePlatformColumnData);
			acc.push(externalPlatformColumnData);
		} else if (alwaysShowBaseColumn) {
			acc.push(basePlatformColumnData);
		}
		return acc;
	}, []);

// function that converts enriched column data objects
const buildScrollableColumn = ({ sourcePlatformPrefix, testPlatformPrefix, key, label, parser, checkBothPlatforms, ...rest }, platform, selectedPlatformKey) => ({
	bind: `${sourcePlatformPrefix}${key}`,
	label,
	onRender: (value, obj) => {
		const test = obj?.[`${testPlatformPrefix}${key}`];
		const parsedSourceValue = parser ? parser(value, platform?.id) : value;
		const parsedTestValue = parser ? parser(test, platform?.id) : test;
		// Currently the values are hardcoded for the only multiplatform options we allow (Prisma, GCM). We can change it in the future to be more dynamic if other platforms are introduced
		let containsError = false;
		if (checkBothPlatforms) {
			if (!parser) {
				const osValue = obj?.[`placementManager_${key}`];
				const gcmValue = obj?.[`externalPlatform_gcm_${key}`];
				const prismaValue = obj?.[`externalPlatform_prisma_${key}`];
				if (osValue !== gcmValue || osValue !== prismaValue) {
					containsError = true;
				}
			} else if (parser && !["ExternalID", "CampaignPK"].includes(key)) {
				const osValue = parser(obj?.[`placementManager_${key}`]);
				const gcmValue = parser(obj?.[`externalPlatform_gcm_${key}`]);
				const prismaValue = parser(obj?.[`externalPlatform_prisma_${key}`]);
				if (osValue !== gcmValue || osValue !== prismaValue) {
					containsError = true;
				}
			}
		}

		// Display an error if the external ID is missing for either platform
		if (parser && ["ExternalID", "CampaignPK"].includes(key)) {
			if (selectedPlatformKey === "GCMPRISMA") {
				const IDColumns = ["Prisma ID", "Prisma Campaign ID", "GCM ID", "GCM Campaign ID"];
				if (IDColumns.includes(label)) {
					const osPrismaValue = parser(obj?.[`placementManager_${key}`], 5);
					const prismaValue = parser(obj?.[`externalPlatform_prisma_${key}`], 5);
					const osGCMValue = parser(obj?.[`placementManager_${key}`], 1);
					const gcmValue = parser(obj?.[`externalPlatform_gcm_${key}`], 1);
					if (!osPrismaValue || !prismaValue || !osGCMValue || !gcmValue) {
						containsError = true;
					}
				}
			} else if (label === "Prisma ID" || label === "Prisma Campaign ID") {
				const osValue = parser(obj?.[`placementManager_${key}`], 5);
				const prismaValue = parser(obj?.[`externalPlatform_prisma_${key}`], 5);
				if (!osValue || !prismaValue) {
					containsError = true;
				}
			} else if (label === "GCM ID" || label === "GCM Campaign ID") {
				const osValue = parser(obj?.[`placementManager_${key}`], 1);
				const gcmValue = parser(obj?.[`externalPlatform_gcm_${key}`], 1);
				if (!osValue || !gcmValue) {
					containsError = true;
				}
			}
		}

		return (
			<CheckForError source={parsedSourceValue} test={parsedTestValue} containsError={containsError}>
				{parsedSourceValue}
			</CheckForError>
		);
	},
	...rest
});

// function that combines all the enriched column data objects into one psuedo sticky column, mimicking multiple sticky columns
const buildStickyColumns = ({ sourcePlatformPrefix, testPlatformPrefix, key, checkBothPlatforms }, obj, platform) => (
	<div className={`csqa-sticky-cell ${sourcePlatformPrefix == platform.prefix && platform.platformStickyClass}`}>
		<CheckForError
			targetClass="csqa-sticky-cell"
			errorClass="csqa-sticky-validationError"
			source={obj?.[`${sourcePlatformPrefix}${key}`]}
			test={obj?.[`${testPlatformPrefix}${key}`]}
			containsError={
				checkBothPlatforms
					? obj?.[`placementManager_${key}`] !== obj?.[`externalPlatform_gcm_${key}`] || obj?.[`placementManager_${key}`] !== obj?.[`externalPlatform_prisma_${key}`]
					: false
			}
		>
			<div style={{ display: "flex", alignItems: "center" }}>
				{obj?.[`${sourcePlatformPrefix}${key}`]?.length > 0 && (
					<>
						<TaxonomyCellRenderer taxonomy={obj?.[`${sourcePlatformPrefix}${key}`]} />
						{obj?.[`${sourcePlatformPrefix}${key}`]?.slice(0, 12)} ...
					</>
				)}
			</div>
		</CheckForError>
	</div>
);

// Function that checks if there is a discrepency between OS and either platform and adds a column for the other platform if it is missing
const checkForEitherDiscrepency = (columns, neccessaryColumns, groupColumns = false) => {
	let columnsCopy = _.cloneDeep(columns);
	const checkedColumns = [];
	columnsCopy.forEach((column) => {
		if (!checkedColumns.includes(column.label)) {
			checkedColumns.push(column.label);
			const gcmError = columnsCopy.find((col) => {
				return col.label === column.label && col.sourcePlatformPrefix === "externalPlatform_gcm_";
			});
			const prismaError = columnsCopy.find((col) => {
				return col.label === column.label && col.sourcePlatformPrefix === "externalPlatform_prisma_";
			});
			if (gcmError && !prismaError) {
				// If there is a discrepency in either platform, this makes all the identical columns in that row into error cells
				columnsCopy.forEach((errorColumn) => {
					if (errorColumn.label === column.label) {
						errorColumn.checkBothPlatforms = true;
					}
				});
				columnsCopy.push({
					checkBothPlatforms: true,
					className: PLATFORM_MAP?.PRISMA?.platformClass,
					key: column.key,
					label: column.label,
					platform: "PRISMA",
					sourcePlatformPrefix: PLATFORM_MAP?.PRISMA?.prefix,
					testPlatformPrefix: PLATFORM_MAP?.OS?.prefix
				});
			} else if (prismaError && !gcmError) {
				columnsCopy.forEach((errorColumn) => {
					if (errorColumn.label === column.label) {
						errorColumn.checkBothPlatforms = true;
					}
				});
				columnsCopy.push({
					checkBothPlatforms: true,
					className: PLATFORM_MAP?.GCM?.platformClass,
					key: column.key,
					label: column.label,
					platform: "GCM",
					sourcePlatformPrefix: PLATFORM_MAP?.GCM?.prefix,
					testPlatformPrefix: PLATFORM_MAP?.OS?.prefix
				});
			}
		}
	});
	// Filters to only include columns that are shared between both platforms
	columnsCopy = columnsCopy.filter((column) => {
		return neccessaryColumns.includes(column.label);
	});
	// Removes any duplicate columns for the OS data
	columnsCopy = [...new Map(columnsCopy.map((column) => [`${column.label}${column.sourcePlatformPrefix}`, column])).values()];
	if (groupColumns) {
		const groupedColumns = [];
		/* This groups identical columns together to make it more readable */
		Object.values(
			columnsCopy?.reduce((accumulator, column) => {
				accumulator[column.label] = accumulator[column.label] || [];
				accumulator[column.label].push(column);
				return accumulator;
			}, Object.create(null))
		)?.forEach((group) => {
			groupedColumns.push(...group);
		});
		return groupedColumns;
	}
	return columnsCopy;
};

// public function called to get the fully constructed columns array for the data table
export default (data, alwaysShowBaseColumn, selectedPlatformKey, tabKey) => {
	try {
		if (data?.length > 0) {
			const platform = PLATFORM_MAP[selectedPlatformKey];
			const discrepantColumnKeys = platform
				? getDiscrepantColumnKeys({
						data,
						platform,
						columns: [...platform.stickyColumns[tabKey], ...platform.scrollableColumns[tabKey]]
					})
				: [];

			let sticky = platform
				? getEnrichedColumnData({
						selectedPlatformKey,
						tabKey,
						columnTypeKey: "stickyColumns",
						discrepantColumnKeys,
						alwaysShowBaseColumn: true
					})
				: [];
			let scrollable = platform
				? getEnrichedColumnData({
						selectedPlatformKey,
						tabKey,
						columnTypeKey: "scrollableColumns",
						discrepantColumnKeys,
						alwaysShowBaseColumn
					})
				: [];

			let multiplePlatforms = [];
			if (!platform) {
				if (selectedPlatformKey === "GCMPRISMA") {
					multiplePlatforms = ["GCM", "PRISMA"];
				}
				multiplePlatforms.forEach((platform) => {
					sticky.push(
						...getEnrichedColumnData({
							selectedPlatformKey: platform,
							tabKey,
							columnTypeKey: "stickyColumns",
							discrepantColumnKeys: getDiscrepantColumnKeys({
								data,
								platform: PLATFORM_MAP[platform],
								columns: [...PLATFORM_MAP[platform].stickyColumns[tabKey], ...PLATFORM_MAP[platform].scrollableColumns[tabKey]]
							}),
							alwaysShowBaseColumn: true
						})
					);
					scrollable.push(
						...getEnrichedColumnData({
							selectedPlatformKey: platform,
							tabKey,
							columnTypeKey: "scrollableColumns",
							discrepantColumnKeys: getDiscrepantColumnKeys({
								data,
								platform: PLATFORM_MAP[platform],
								columns: [...PLATFORM_MAP[platform].stickyColumns[tabKey], ...PLATFORM_MAP[platform].scrollableColumns[tabKey]]
							}),
							alwaysShowBaseColumn
						})
					);
				});

				/* Filter out columns to only the specific ones shared between GCM and PRISMA */
				if (selectedPlatformKey === "GCMPRISMA") {
					if (tabKey === "campaign") {
						sticky = checkForEitherDiscrepency(sticky, GCM_PRISMA_COLUMNS.campaignColumns);
						scrollable = checkForEitherDiscrepency(scrollable, GCM_PRISMA_COLUMNS.campaignColumns, true);
						scrollable = scrollable.sort((column) => {
							if (["GCM Campaign ID", "Prisma Campaign ID"].includes(column.label)) {
								return -1;
							}
							return 1;
						});
					} else if (tabKey === "packagesPlacements") {
						sticky = checkForEitherDiscrepency(sticky, GCM_PRISMA_COLUMNS.packagePlacementColumns, true);
						scrollable = checkForEitherDiscrepency(scrollable, GCM_PRISMA_COLUMNS.packagePlacementColumns, true);
						scrollable = scrollable.sort((column) => {
							if (["GCM ID", "Prisma ID"].includes(column.label)) {
								return -1;
							}
							return 1;
						});
					}
				}
			}

			// map the enriched sticky & scrollable column data to actual column objects, ready to be rendered by the data table.
			return [
				{
					sticky: true,
					minWidth: true,
					className: "csqa-external-platform-column-tx-4",
					label: (
						<div className="csqa-tx-header">
							{sticky.map(({ label }) => (
								<div className="csqa-tx-header-label">{label}</div>
							))}
						</div>
					),
					onRender: (val, obj) => <>{sticky.map((column) => buildStickyColumns(column, obj, PLATFORM_MAP[column.platform]))}</>
				},
				...scrollable.map((column) => buildScrollableColumn(column, PLATFORM_MAP[column.platform], selectedPlatformKey))
			];
		}
		return [];
	} catch (e) {
		console.log(e);
		return [];
	}
};
